import { TogetherAuctionDIPEntityOperationNames } from '../conf/app.conf';
import { Injectable } from '@angular/core';
import { TogetherAuctionDIPEntityState, TogetherAuctionDIPServiceState, TogetherAuctionDIPEntityOperation } from './../models/enums';
import { Observable, BehaviorSubject } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable()
export class EntityStateService {

  /**
   * BehaviourSubject (Angular Rx Subject) for if an entity changes.
   *
   * @type {BehaviorSubject<Array<String,TogetherAuctionDIPEntityState>>}
   * @memberof DipStateService
   */
  private allEntityStateChanges: BehaviorSubject<[string, TogetherAuctionDIPEntityState]> =
    new BehaviorSubject<[string, TogetherAuctionDIPEntityState]>(['Init', TogetherAuctionDIPEntityState.OperationIdle]);

  /**
   * Map used to store the status of a state.
   *
   * @private
   * @type {Map<String,TogetherAuctionDIPEntityState>}
   * @memberof DipStateService
   */
  private loadingEntities: Map<String, TogetherAuctionDIPEntityState> =
    new Map<String, TogetherAuctionDIPEntityState>();

  /**
   * Map of commands to specific entities.
   *
   * @private
   * @type {Map<String,TogetherAuctionDIPEntityOperation>}
   * @memberof DipStateService
   */
  private stateCommands: Map<String, TogetherAuctionDIPEntityOperation> =
    new Map<String, TogetherAuctionDIPEntityOperation>();

  /**
   * Command from a parent to all children.
   *
   * @private
   * @type {BehaviorSubject<[string,TogetherAuctionDIPEntityOperation]>}
   * @memberof EntityStateService
   */
  private multicastStateCommand: BehaviorSubject<[string, TogetherAuctionDIPEntityOperation]> =
    new BehaviorSubject<[string, TogetherAuctionDIPEntityOperation]>(['Init', TogetherAuctionDIPEntityOperation.None]);

  /**
   * Command going from a child to a parent.
   *
   * @private
   * @type {BehaviorSubject<[string,TogetherAuctionDIPEntityState]>}
   * @memberof EntityStateService
   */
  private singlecastStateCommand: BehaviorSubject<[string, TogetherAuctionDIPEntityState, any]> =
    new BehaviorSubject<[string, TogetherAuctionDIPEntityState, any]>(['Init', TogetherAuctionDIPEntityState.OperationIdle, '']);
  /**
   * Checks if the loading entities map has entities within it.
   *
   * @readonly
   * @memberof DipStateService
   */
  get hasEntities() {
    return this.loadingEntities.size > 0;
  }

  /**
   * Returns an array of states of each component.
   *
   * @readonly
   * @type {Array<TogetherAuctionDIPEntityState>}
   * @memberof DipStateService
   */
  get entityStates(): Array<TogetherAuctionDIPEntityState> {

    // Get values from the entity list.
    const leValues: any = Array.from(this.loadingEntities);

    // Transform into an array of statuses.
    return _.transform(leValues, (iter, n) => {

      // Check to see if something went pear shaped for an unknown reason..
      if (_.isEmpty(n) || _.isArray(n) && n.length !== 2) {
        return true;
      }

      const state = n[1];

      if (state === TogetherAuctionDIPEntityState.OperationPending) {
        iter.push(state);
      }
      return true;
    }, []);
  }


  /**
   * Getter for the current state of this service, as instructed by entities feeding states in.
   *
   * @readonly
   * @type {TogetherAuctionDIPServiceState}
   * @memberof DipStateService
   */
  get currentEntityState(): TogetherAuctionDIPServiceState {

    // If the loadingEntities map is empty, then, welp, no, we're not loading.
    if (this.hasEntities) {
      return TogetherAuctionDIPServiceState.Idle;
    }

    // Are there any pending?
    const loadingStates = this.entityStates;

    // If there are any elements loading in the transform above, show loading, else
    return loadingStates.indexOf(TogetherAuctionDIPEntityState.OperationPending) !== -1 ?
      TogetherAuctionDIPServiceState.PerformingOperation : TogetherAuctionDIPServiceState.Idle;
  }


  /**
   * Getter for if entities have errors.
   *
   * @readonly
   * @type {boolean}
   * @memberof DipStateService
   */
  get doEntitiesHaveErrors(): boolean {

    // If the loadingEntities map is empty, then, welp, no, we're not loading.
    if (this.hasEntities) {
      return false;
    }

    // Any errors ?
    const loadingStates = this.entityStates;

    // Does the array above have any errors within it?
    return loadingStates.indexOf(TogetherAuctionDIPEntityState.Error) !== -1;
  }


  /**
   * Adds a state command
   *
   * @param {String} element
   * @param {TogetherAuctionDIPEntityOperation} operation
   * @memberof DipStateService
   */
  addStateCommand(element: string, operation: TogetherAuctionDIPEntityOperation): void {

    // Set the element w/ operation.
    this.stateCommands.set(element, operation);

    // Emit an output to say that the state command was set.
    this.multicastStateCommand.next([element, this.stateCommands.get(element)]);

    // Remove the command from the element.
    this.stateCommands.delete(element);
  }

  /**
   * Issues a singlecast command.
   *
   * @param {string} element
   * @param {TogetherAuctionDIPEntityOperation} operation
   * @memberof EntityStateService
   */
  issueSingleCastCommand(element: string, operation: TogetherAuctionDIPEntityState, payload: any) {
    this.singlecastStateCommand.next([element, operation, payload]);
  }


  /**
   * Subscribes to a one-way command coming from a sub-component.
   *
   * @memberof EntityStateService
   */
  subscribeToSingleCastCommand() {
    return new Observable((reqObs) => {
      return this.singlecastStateCommand.subscribe(
        (success) => {
          reqObs.next(success);
        },
        (error) => {
          reqObs.error(error);
        }
      )
    });
  }

  /**
   * Subscribe to the change of a state command.
   *
   * @param {string} elementNames
   * @param {boolean} [all=true]
   * @returns
   * @memberof DipStateService
   */
  subscribeToStateCommand(elementNames: string, all: boolean = true): Observable<TogetherAuctionDIPEntityOperation> {
    let elements = all ? elementNames.concat("*ANY*") : elementNames;
    const self = this;
    return new Observable((reqObs) => {
      this.multicastStateCommand.subscribe(
        success => {

          // Get the element from the command.
          const element = _.toString(success[0]);

          // If it's an init command or the elements don't exist.
          if (element === "Init" || elements.indexOf(element) === -1) {
            return;
          }

          // Otherwise, let's give you the intended
          reqObs.next(success[1]);
        },
        error => {
          reqObs.error(error);
        }
      );
    });
  }

  /**
   * Wait for selected elements to finish loading.
   *
   * @param {Array<String>} entityNames
   * @memberof EntityStateService
   */
  waitForElementsToFinishLoading(entityNames: Array<string>): Observable<boolean> {

    // It's disliked, but hey, without it, won't work. Sorry.
    const self = this;

    // Create observable.
    return new Observable<boolean>((reqObs) => {
      return self.allEntityStateChanges.subscribe(
        (success) => {
          const loading = self.checkElementsLoading(entityNames);
          if (!loading) {
            reqObs.complete();
          }
        },
        (error) => {
          reqObs.error(error);
        }
      );
    });
  }

  /**
   * Issues a command but we don't care if it returns anything.
   *
   * @param {string} element
   * @param {TogetherAuctionDIPEntityOperation} operation
   * @memberof EntityStateService
   */
  issueNoReturnStateCommand(element: string, operation: TogetherAuctionDIPEntityOperation): void {
    this.addStateCommand(element, operation);
  }

  /**
   * Issues state command, and does all the stuff inbetween.
   *
   * @param {string} element
   * @param {TogetherAuctionDIPEntityOperation} operation
   * @memberof EntityStateService
   */
  issueStateCommand(entities: Array<string>, element: string, operation: TogetherAuctionDIPEntityOperation): Observable<boolean> {

    // Add the state command.
    this.addStateCommand(element, operation);

    // Now wait for the elements to finish loading
    return this.waitForElementsToFinishLoading(entities);
  }

  /**
   * Subscribe to the changes on a single entity.
   *
   * @param {String} entityName
   *  The name of the entity.
   * @returns {Observable<TogetherAuctionDIPEntityState>}
   *  An observable which listens to all changes on this service, filtering for this entity.
   * @memberof DipStateService
   */
  subscribeToEntityChange(entityName: String): Observable<TogetherAuctionDIPEntityState> {
    // Preserve state because we can't access it in other browsers.
    const self = this;

    // Return an observable, in which we do our magic.
    return new Observable<TogetherAuctionDIPEntityState>((reqObs) => {
      return self.allEntityStateChanges.subscribe(
        (success) => {
          if (success[0] === entityName) {
            reqObs.next(success[1]);
          }
        },
        (error) => {
          if (error[0] === entityName) {
            reqObs.error(error[1]);
          }
        },
      );
    });
  }


  /**
   * Subscribe to the changes of several entities.
   *
   * @param {Array<String>} entityNames
   *  An array of the names of the entities.
   * @returns {Observable<[String,TogetherAuctionDIPEntityState]>}
   *  An observable which listens to all changes on this service, filtering for this entity.
   * @memberof DipStateService
   */
  subscribeToEntitiesChanged(entityNames: Array<String>): Observable<[String, TogetherAuctionDIPEntityState]> {
    const self = this;

    return new Observable<[String, TogetherAuctionDIPEntityState]>((reqObs) => {
      return self.allEntityStateChanges.subscribe(
        (success) => {
          if (entityNames.indexOf(success[0]) !== -1) {
            reqObs.next(success);
          }
        },
        (error) => {
          if (entityNames.indexOf(error[0]) !== -1) {
            reqObs.error(error);
          }
        },
      );
    });
  }

  /**
   * Checks if elements are marked as loading in the map.
   *
   * @param {Array<string>} [entityNames=null]
   *  The names of the entities.
   * @returns {boolean}
   * @memberof DipStateService
   */
  checkElementsLoading(entityNames: Array<string> = null): boolean {

    // Get the values from the map.
    const vals = Array.from(this.loadingEntities.values());

    // If the entities are set, we need separate logic.
    if (!_.isNil(entityNames)) {

      const isLoading = _.transform(entityNames, (iter: Array<any>, n: string) => {

        // Val from map.
        const mapVal = this.loadingEntities.get(n);

        // Does it have the map?
        if (_.isNil(mapVal)) {
          return false;
        }

        const hasElem = mapVal !== TogetherAuctionDIPEntityState.OperationPending;
        if (hasElem) {
          iter.push(n);
        }
        return hasElem;
      }, []);

      // Now, check if the elements in the transform are the same as the entityNames
      const intersectEntities = _.intersection(isLoading, entityNames);

      // Return bool if the loading array is empty (Inverted so it matches name of function)
      return intersectEntities.length !== entityNames.length;
    }

    // Other, check if all elements are loading if null.
    return vals.indexOf(TogetherAuctionDIPEntityState.OperationPending) === -1;
  }

  /**
   * Observable for a entity to subscribe to to see if the
   *
   * @param {Array<string>} [entityNames=null]
   * @returns {Observable<boolean>}
   * @memberof DipStateService
   */
  subscribeWaitForLoadingComplete(entityNames: Array<string> = null): Observable<boolean> {
    return new Observable<boolean>((reqObs) => {
      this.allEntityStateChanges.subscribe(
        (success) => {
          if (this.checkElementsLoading(entityNames)) {
            reqObs.next(true);
          }
        },
        (error) => {
          if (this.checkElementsLoading(entityNames)) {
            reqObs.next(true);
          }
        },
      );
    });
  }

  /**
   * Tells the service how many entities are loading.
   *
   * @param {any} entityNames
   *  The names of the entities which are being pre-loaded.
   * @memberof DipStateService
   */
  setEntitiesLoading(entityNames: Array<string>) {
    // Loop each and set in the map.
    _.each(entityNames, (name) => {
      // Default all as idle, as we assume they do nothing from start.
      this.loadingEntities.set(name, TogetherAuctionDIPEntityState.OperationIdle);
    });
  }

  /**
   * Returns boolean relatingto if a single entity is loading.
   *
   * @param {String} entityName
   * @returns
   * @memberof DipStateService
   */
  isEntityLoading(entityName: String) {
    return this.loadingEntities.get(entityName) || false;
  }

  /**
   * Check if selected entities are loading.
   *
   * @param {Array<string>} entityNames
   *  Names of entities to check.
   * @returns {Boolean}
   *  Are those entities loading?
   * @memberof DipStateService
   */
  areEntitiesLoading(entityNames: Array<string>) {
    const entitiesLoading = _.transform(entityNames, (iter, n) => {

      // Get the value from the map.
      const val = this.loadingEntities.get(n);

      if (val === TogetherAuctionDIPEntityState.OperationPending) {
        iter.push(n);
      }
      return true;
    }, []);

    return entitiesLoading.length > 0;
  }



  /**
   * Outputs using subject to give error if state is errored or value if changed to non-error.
   *
   * @param {String} entityName
   * @param {TogetherAuctionDIPEntityState} status
   * @memberof DipStateService
   */
  emitEntityState(entityName: string, status: TogetherAuctionDIPEntityState) {
    if (status === TogetherAuctionDIPEntityState.Error) {
      this.allEntityStateChanges.error([
        entityName,
        status,
      ]);
    } else {
      this.allEntityStateChanges.next([
        entityName,
        status,
      ]);
    }
  }

  /**
   * Sets the loading status of a given entity.
   *
   * @param {String} entityName
   *  The name of the entity.
   * @param {TogetherAuctionDIPEntityState} status
   *  The status last set by the entity or defaulted as idle.
   * @memberof DipStateService
   */
  setEntityState(entityName: string, status: TogetherAuctionDIPEntityState) {
    this.loadingEntities.set(entityName, status);
    this.emitEntityState(entityName, status);
  }

  /**
   * Gets the loading status of an entity.
   *
   * @param {String} entityName
   *  The name of the entity.
   * @param {TogetherAuctionDIPEntityState} status
   *  The status last set by the entity or defaulted as idle.
   * @memberof DipStateService
   */
  getEntityState(entityName: string) {
    this.loadingEntities.get(entityName) || TogetherAuctionDIPEntityState.OperationIdle;
  }

  constructor() { }

}
